import { useState } from "react";
import { useCurrentLang } from "@/i18n/language";
import type { RiceBlock, RiceContent } from "@/pages/bali-info-detail/data/types";

type OpenPhoto = { src: string; alt: string } | null;

function SplitBlock({
  block,
  imageLeft,
  onOpen,
}: {
  block: Extract<RiceBlock, { type: "split" }>;
  imageLeft: boolean;
  onOpen: (src: string, alt: string) => void;
}) {
  const lang = useCurrentLang();

  return (
    <div
      className={`flex flex-col md:flex-row gap-4 md:gap-5 items-start ${
        imageLeft ? "md:flex-row" : "md:flex-row-reverse"
      }`}
    >
      <button
        type="button"
        onClick={() => onOpen(block.image, block.imageAlt[lang])}
        className="group relative w-full md:w-72 h-52 md:h-48 rounded-lg overflow-hidden bg-background-200 flex-shrink-0 cursor-pointer"
      >
        <img
          src={block.image}
          alt={block.imageAlt[lang]}
          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
        />
        <span className="absolute bottom-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-foreground-950/55 text-background-50 opacity-0 group-hover:opacity-100 transition-opacity">
          <i className="ri-zoom-in-line text-xs"></i>
        </span>
      </button>
      <p className="text-sm text-foreground-700 leading-relaxed flex-1 min-w-0">
        {block.text[lang]}
      </p>
    </div>
  );
}

function BlockList({
  blocks,
  onOpen,
}: {
  blocks: RiceBlock[];
  onOpen: (src: string, alt: string) => void;
}) {
  const lang = useCurrentLang();

  return (
    <div className="space-y-5">
      {blocks.map((block, index) => {
        if (block.type === "text") {
          return (
            <p
              key={`text-${index}`}
              className="text-sm text-foreground-700 leading-relaxed"
            >
              {block.text[lang]}
            </p>
          );
        }

        if (block.type === "split") {
          return (
            <SplitBlock
              key={`split-${index}`}
              block={block}
              imageLeft={index % 2 === 1}
              onOpen={onOpen}
            />
          );
        }

        if (block.type === "image") {
          return (
            <figure key={`image-${index}`} className="w-full">
              <button
                type="button"
                onClick={() => onOpen(block.image, block.imageAlt[lang])}
                className="group relative block w-full h-52 md:h-64 rounded-lg overflow-hidden bg-background-200 cursor-pointer"
              >
                <img
                  src={block.image}
                  alt={block.imageAlt[lang]}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
              </button>
              {block.caption ? (
                <figcaption className="text-xs text-foreground-500 mt-2">
                  {block.caption[lang]}
                </figcaption>
              ) : null}
            </figure>
          );
        }

        return (
          <div
            key={`entries-${index}`}
            className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4"
          >
            {block.entries.map((entry) => (
              <div
                key={entry.name.de}
                className="bg-background-100 border border-background-200 rounded-lg p-4 md:p-5"
              >
                <h4 className="font-heading text-base text-foreground-950">
                  {entry.name[lang]}
                </h4>
                {entry.latin ? (
                  <p className="text-xs text-accent-700 italic mt-0.5 mb-2">
                    {entry.latin}
                  </p>
                ) : null}
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {entry.text[lang]}
                </p>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export default function RiceArticle({ content }: { content: RiceContent }) {
  const lang = useCurrentLang();
  const [openPhoto, setOpenPhoto] = useState<OpenPhoto>(null);

  const scrollToSection = (index: number) => {
    const el = document.getElementById(`rice-section-${index}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div>
      <div className="rounded-lg border border-accent-200 bg-accent-50/70 p-5 md:p-6">
        <p className="text-sm text-foreground-700 leading-relaxed">
          {content.intro[lang]}
        </p>
      </div>

      {content.introNote ? (
        <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-900 flex-shrink-0">
              <i className="ri-water-flash-line text-sm"></i>
            </div>
            <h3 className="font-heading text-base text-foreground-950">
              {content.introNote.title[lang]}
            </h3>
          </div>
          <p className="text-sm text-foreground-700 leading-relaxed">
            {content.introNote.text[lang]}
          </p>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2 my-8">
        {content.sections.map((section, index) => (
          <button
            type="button"
            key={`jump-${section.title.de}`}
            onClick={() => scrollToSection(index)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-background-200 bg-background-50 text-xs text-foreground-700 hover:border-accent-500 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-down-line"></i>
            {section.title[lang]}
          </button>
        ))}
      </div>

      <div className="space-y-12">
        {content.sections.map((section, index) => (
          <section
            key={section.title.de}
            id={`rice-section-${index}`}
            className="scroll-mt-28"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-900 flex-shrink-0">
                <i className={`${section.icon} text-base`}></i>
              </div>
              <h3 className="font-heading text-xl text-foreground-950 leading-snug pt-1">
                {section.title[lang]}
              </h3>
            </div>
            <BlockList
              blocks={section.blocks}
              onOpen={(src, alt) => setOpenPhoto({ src, alt })}
            />
          </section>
        ))}
      </div>

      {openPhoto ? (
        <div
          className="fixed inset-0 z-[60] bg-foreground-950/85 flex items-center justify-center p-4 md:p-10"
          onClick={() => setOpenPhoto(null)}
          role="presentation"
        >
          <div className="relative max-w-4xl w-full">
            <img
              src={openPhoto.src}
              alt={openPhoto.alt}
              className="w-full max-h-[80vh] object-contain rounded-lg"
            />
            <p className="text-xs text-background-50/80 text-center mt-3">
              {openPhoto.alt}
            </p>
            <button
              type="button"
              onClick={() => setOpenPhoto(null)}
              aria-label="Schließen"
              className="absolute -top-3 -right-3 w-10 h-10 flex items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700 cursor-pointer shadow-lg transition-colors"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}